import { useEffect, useState } from "react";
import { FileText, History as HistoryIcon, Download, Paperclip, CalendarRange, X, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { StatusBadge } from "../components/StatusBadge";
import { History } from "../components/History";
import { VoucherDialog } from "../components/VoucherDialog";
import { Attachments } from "../components/Attachments";

const FILTERS = [["", "all"], ["received_from_supplier", "received_from_supplier"], ["received_from_inventory", "received_from_inventory"], ["returned_to_supplier", "returned_to_supplier"]];

export default function Records() {
  const { t, fmt, lang } = useI18n();
  const [cars, setCars] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [hist, setHist] = useState(null);
  const [voucher, setVoucher] = useState(null);
  const [invoicesCar, setInvoicesCar] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [range, setRange] = useState({ from: "", to: "" });

  const params = () => ({
    q: q || undefined,
    status: status || undefined,
    start: range.from ? new Date(`${range.from}T00:00:00`).toISOString() : undefined,
    end: range.to ? new Date(`${range.to}T23:59:59.999`).toISOString() : undefined,
  });

  useEffect(() => {
    const id = setTimeout(() => api.get("/cars", { params: params() }).then((r) => setCars(r.data)), 250);
    return () => clearTimeout(id);
  }, [q, status, range]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportExcel = async () => {
    try {
      const { data } = await api.get("/cars/export", { params: { ...params(), lang }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `records_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="text-4xl sm:text-5xl font-extrabold">{t("records")}</h1>
        <Button data-testid="export-excel-btn" onClick={exportExcel} className="bg-[#F5BE25] hover:bg-[#E5AC12] text-[#3E3E40]"><Download className="w-4 h-4" />{t("exportExcel")}</Button>
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        <Input className="max-w-sm bg-white" placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="records-search-input" />
        {FILTERS.map(([v, l]) => (
          <button key={l} data-testid={`records-filter-${l}`} onClick={() => setStatus(v)}
            className={`px-4 py-1.5 rounded-full text-sm font-bold border transition-colors ${status === v ? "bg-[#3E3E40] text-white border-[#3E3E40]" : "bg-white text-slate-700 hover:bg-slate-100"}`}>{t(l)}</button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="records-date-filter">
        <CalendarRange className="w-4 h-4 text-slate-500" />
        <span className="font-semibold text-slate-600">{t("fromDate")}</span>
        <Input type="date" dir="ltr" className="w-auto bg-white" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} data-testid="records-from-input" />
        <span className="font-semibold text-slate-600">{t("toDate")}</span>
        <Input type="date" dir="ltr" className="w-auto bg-white" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} data-testid="records-to-input" />
        <Button size="sm" data-testid="records-date-search" onClick={() => setRange({ from, to })}><Search className="w-4 h-4" />{t("searchBtn")}</Button>
        {(from || to || range.from || range.to) && <Button size="sm" variant="ghost" data-testid="records-date-clear" onClick={() => { setFrom(""); setTo(""); setRange({ from: "", to: "" }); }}><X className="w-4 h-4" />{t("clearDates")}</Button>}
      </div>
      <div className="bg-white border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            {["plate", "carType", "supplier", "shelf", "status", "date", "actions"].map((h) => <TableHead key={h} className="text-start">{t(h)}</TableHead>)}
          </TableRow></TableHeader>
          <TableBody>
            {cars.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-10">{t("noData")}</TableCell></TableRow>}
            {cars.map((c) => (
              <TableRow key={c.id} data-testid={`record-row-${c.id}`}>
                <TableCell><span className="plate text-xs">{c.plate_number}</span></TableCell>
                <TableCell>{c.car_type}</TableCell>
                <TableCell>{c.supplier_name}</TableCell>
                <TableCell className="font-bold">{c.shelf_number}</TableCell>
                <TableCell><StatusBadge status={c.status} /></TableCell>
                <TableCell className="text-sm whitespace-nowrap">{fmt(c.status_date)}</TableCell>
                <TableCell className="flex flex-wrap gap-1">
                  <Button size="sm" variant="ghost" data-testid={`record-history-${c.id}`} onClick={() => setHist(c)}><HistoryIcon className="w-4 h-4" /></Button>
                  {c.invoice_count > 0 && (
                    <Button size="sm" variant="outline" data-testid={`record-invoices-${c.id}`} className="border-[#F5BE25] bg-amber-50 hover:bg-amber-100" onClick={() => setInvoicesCar(c)}>
                      <Paperclip className="w-4 h-4" />{t("invoiceShort")}{c.invoice_count > 1 ? ` (${c.invoice_count})` : ""}
                    </Button>
                  )}
                  {(c.vouchers || []).map((v) => (
                    <Button key={v.number} size="sm" variant="outline" data-testid={`record-voucher-${v.number}`} onClick={() => setVoucher({ car: c, voucher: v })}><FileText className="w-4 h-4" />{v.number}</Button>
                  ))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={!!hist} onOpenChange={(o) => !o && setHist(null)}>
        <DialogContent data-testid="history-dialog">
          <DialogHeader><DialogTitle>{t("history")} · <span className="plate text-sm">{hist?.plate_number}</span></DialogTitle></DialogHeader>
          {hist && <History items={hist.history} />}
        </DialogContent>
      </Dialog>
      <Dialog open={!!invoicesCar} onOpenChange={(o) => !o && setInvoicesCar(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="record-invoices-dialog">
          <DialogHeader><DialogTitle>{t("invoices")} · <span className="plate text-sm">{invoicesCar?.plate_number}</span></DialogTitle></DialogHeader>
          {invoicesCar && <Attachments carId={invoicesCar.id} voucherNumber="invoices" title={t("invoices")} testId="record-invoices-section" />}
        </DialogContent>
      </Dialog>
      <VoucherDialog data={voucher} onOpenChange={(o) => !o && setVoucher(null)} />
    </div>
  );
}
